import type React from 'react'
import { ImageUrlOptions } from '@frontend/core/lib/modules/images/types'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { useImageUrl } from './hooks'
import { useState } from 'react'

interface StaticAssetImageProps {
  src?: string | null
  alt: string
  fallback?: React.ReactNode
  preset?: ImageUrlOptions
  className?: string
  onLoad?: () => void
  onError?: () => void
}

/**
 * Component that displays images from the static asset API with consistent URL generation
 * Ensures proper caching across components by using the same URL building logic
 */
export function StaticAssetImage({
  src,
  alt,
  fallback,
  preset = IMAGE_PRESETS.playerLarge,
  className,
  onLoad,
  onError
}: StaticAssetImageProps) {
  const [hasError, setHasError] = useState(false)

  const getImageUrl = useImageUrl()
  const imageUrl = getImageUrl(src, preset)

  if (hasError) {
    // Show fallback if there was an error loading the image
    return <>{fallback}</>
  }

  // Show fallback if no URL can be generated
  if (!imageUrl && fallback) {
    return <>{fallback}</>
  }

  // Return null if no URL and no fallback
  if (!imageUrl) {
    return null
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={`object-cover ${className}`}
      onLoad={onLoad}
      onError={() => {
        setHasError(true)
        if (onError) {
          onError()
        }
      }}
    />
  )
}
